import { KText } from '@/ui-library/components';
import {
  ApplicationCreator,
  CreditedTo,
  DepartmentName,
  HiredDate,
  HiringManagers,
  JobName,
  NameCell,
  OfficeName,
  OfferCreatedDate,
  OfferStatus,
  Recruiters,
  SourceName,
  StageName,
  StartDate,
  TimeInStage,
  TimeToHire,
  TimeToOffer,
  WorkType,
  Workplace,
} from '../cells/LiveJobPipelineDrilldownCells';

const renderDrilldownHeader = (title: string) => () => (
  <KText size="sm-medium" color="secondary-900" textTransform="none">
    {title}
  </KText>
);

export const useLiveJobPipelineDrilldownConfig = ({
  drilldownHeading = '',
  onSort: _onSort = () => {},
  currentSort: _currentSort = {},
}) => {
  const getColumnConfig = () => {
    const columnsMap = {
      applicantName: {
        title: 'Applicant name',
        Header: renderDrilldownHeader('Applicant name'),
        id: 'candidate_name',
        width: 220,
        accessor: 'name',
        sort: true,
        filterable: true,
        columnConfig: false,
        Cell: ({ row }) => NameCell(row),
      },
      timeInStage: {
        title: 'Time in stage',
        Header: renderDrilldownHeader('Time in stage'),
        minWidth: 140,
        accessor: 'time_in_stage',
        sort: true,
        Cell: ({ row }) => TimeInStage(row),
      },
      sourceName: {
        title: 'Source',
        Header: renderDrilldownHeader('Source'),
        minWidth: 220,
        accessor: 'appl_contact_source',
        id: 'appl_contact_source',
        sort: true,
        Cell: ({ row }) => SourceName(row),
      },
      creditedTo: {
        title: 'Credited to',
        Header: renderDrilldownHeader('Credited to'),
        minWidth: 200,
        accessor: 'credited_to',
        sort: true,
        Cell: ({ row }) => CreditedTo(row),
      },
      jobName: {
        title: 'Job name',
        Header: renderDrilldownHeader('Job name'),
        width: 200,
        accessor: 'job_name',
        sort: true,
        filterable: true,
        Cell: ({ row }) => JobName(row),
      },
      stageName: {
        title: 'Stage name',
        Header: renderDrilldownHeader('Stage name'),
        minWidth: 200,
        accessor: 'stage_name',
        sort: true,
        Cell: ({ row }) => StageName(row),
      },
      recruiters: {
        title: 'Recruiters',
        Header: renderDrilldownHeader('Recruiters'),
        minWidth: 220,
        accessor: 'recruiters',
        sort: true,
        Cell: ({ row }) => Recruiters(row),
      },
      hiringManagers: {
        title: 'Hiring managers',
        Header: renderDrilldownHeader('Hiring managers'),
        minWidth: 220,
        accessor: 'hiring_managers',
        sort: true,
        Cell: ({ row }) => HiringManagers(row),
      },
      departmentName: {
        title: 'Department',
        Header: renderDrilldownHeader('Department'),
        minWidth: 200,
        accessor: 'department_name',
        sort: true,
        Cell: ({ row }) => DepartmentName(row),
      },
      officeName: {
        title: 'Offices',
        Header: renderDrilldownHeader('Offices'),
        minWidth: 220,
        accessor: 'offices',
        sort: true,
        Cell: ({ row }) => OfficeName(row),
      },
      workplace: {
        title: 'Workplace',
        Header: renderDrilldownHeader('Workplace'),
        minWidth: 200,
        accessor: 'workplace',
        sort: true,
        Cell: ({ row }) => Workplace(row),
      },
      workType: {
        title: 'Work type',
        Header: renderDrilldownHeader('Work type'),
        minWidth: 200,
        accessor: 'work_type',
        sort: true,
        Cell: ({ row }) => WorkType(row),
      },
      applicationCreator: {
        title: 'Application creator',
        Header: renderDrilldownHeader('Application creator'),
        minWidth: 200,
        accessor: 'application_creator',
        sort: true,
        Cell: ({ row }) => ApplicationCreator(row),
      },
      offerCreatedDate: {
        title: 'Offer created date',
        Header: renderDrilldownHeader('Offer created date'),
        minWidth: 160,
        accessor: 'offer_created_date',
        sort: true,
        Cell: ({ row }) => OfferCreatedDate(row),
      },
      offerStatus: {
        title: 'Offer acceptance status',
        Header: renderDrilldownHeader('Offer acceptance status'),
        minWidth: 200,
        accessor: 'offer_status',
        sort: true,
        Cell: ({ row }) => OfferStatus(row),
      },
      timeToOffer: {
        title: 'Time to offer',
        Header: renderDrilldownHeader('Time to offer'),
        minWidth: 140,
        accessor: 'time_to_offer',
        sort: true,
        Cell: ({ row }) => TimeToOffer(row),
      },
      startDate: {
        title: 'Start date',
        Header: renderDrilldownHeader('Start date'),
        minWidth: 140,
        accessor: 'start_date',
        sort: true,
        Cell: ({ row }) => StartDate(row),
      },
      hiredDate: {
        title: 'Hired date',
        Header: renderDrilldownHeader('Hired date'),
        minWidth: 140,
        accessor: 'hired_date',
        sort: true,
        Cell: ({ row }) => HiredDate(row),
      },
      timeToHire: {
        title: 'Time to hire',
        Header: renderDrilldownHeader('Time to hire'),
        minWidth: 140,
        accessor: 'time_to_hire',
        sort: true,
        Cell: ({ row }) => TimeToHire(row),
      },
    };

    const commonColumns = [
      columnsMap.jobName,
      columnsMap.stageName,
      columnsMap.recruiters,
      columnsMap.hiringManagers,
      columnsMap.departmentName,
      columnsMap.officeName,
      columnsMap.workplace,
      columnsMap.workType,
      columnsMap.applicationCreator,
    ];

    // Application Review & Interview stages: Applicant name, Time in stage, common

    // Offer stage: + Offer created date, Offer acceptance status, Time to offer, Start date
    if (
      drilldownHeading === 'offer' ||
      drilldownHeading === 'Currently in offer stages' ||
      drilldownHeading === 'offer_total_count' ||
      drilldownHeading === 'offer_actual_count'
    ) {
      return [
        columnsMap.applicantName,
        columnsMap.offerCreatedDate,
        columnsMap.offerStatus,
        columnsMap.timeToOffer,
        columnsMap.startDate,
        columnsMap.sourceName,
        columnsMap.creditedTo,
        columnsMap.timeInStage,
        ...commonColumns,
      ];
    }

    // Hired stage: + Hired date, Time to hire, Start date
    if (
      drilldownHeading === 'hired' ||
      drilldownHeading === 'Currently in hired stages' ||
      drilldownHeading === 'hired_total_count' ||
      drilldownHeading === 'hired_actual_count'
    ) {
      return [
        columnsMap.applicantName,
        columnsMap.hiredDate,
        columnsMap.timeToHire,
        columnsMap.startDate,
        columnsMap.sourceName,
        columnsMap.creditedTo,
        columnsMap.timeInStage,
        ...commonColumns,
      ];
    }

    // Unified view / unknown: show conditional columns (offer + hired fields)
    return [
      columnsMap.applicantName,
      columnsMap.timeInStage,
      columnsMap.sourceName,
      columnsMap.creditedTo,
      ...commonColumns,
    ];
  };

  return { columnConfig: getColumnConfig() };
};
